/**
 * The API client.
 *
 * Two things here that the rest of the app depends on:
 *
 *   1. Every mutating call carries an `x-client-op-id` (a UUIDv7 minted here),
 *      so a retry after a dropped connection cannot double-post. The Worker
 *      records it in `sync_operations`. This matters most for advances: the same
 *      request sent twice must produce one cash receipt.
 *   2. A 409 carries the current row. Callers merge and retry rather than
 *      clobbering somebody else's write — the owner approving while the clerk is
 *      still typing is the normal case, not an edge one.
 */

import { noteOffline, noteOnline } from './connectivity';

/**
 * Where the Worker is.
 *
 * In development this stays `/api` and Vite proxies it to the local Worker, so
 * the browser sees one origin. In production the front end is on Pages and the
 * Worker is on workers.dev — two origins — so the build passes the Worker's full
 * base in `VITE_API_BASE` and CORS does the rest, which is what
 * `ALLOWED_ORIGINS` in `api/wrangler.toml` is for. Keep the two in step: the
 * Worker only answers a browser whose origin it lists.
 */
const BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? '/api';

/**
 * The full URL of an API path, for the few places that fetch outside
 * `request()` — the signature and receipt images. A hard-coded `/api/…` there
 * works in development (Vite proxies it) and fails silently in production:
 * pages.dev answers any unknown path with the app's own HTML and a 200, so the
 * "image" that comes back is a web page and the phone shows a broken picture.
 * Found on the real phone, 23 September 2026.
 */
export function apiUrl(path: string): string {
  return `${BASE}${path}`;
}

/** Fetch for a stored image: the token, the tablet header, and a check that it IS an image. */
export async function fetchImage(path: string): Promise<Blob> {
  const headers: Record<string, string> = {};
  if (sessionToken) headers.authorization = `Bearer ${sessionToken}`;
  if (tabletCredential) headers['x-tablet-credential'] = tabletCredential;
  const response = await send(apiUrl(path), { headers });
  if (!response.ok) throw new Error(String(response.status));
  const type = response.headers.get('content-type') ?? '';
  if (!type.startsWith('image/')) throw new Error(`not an image: ${type || 'no content-type'}`);
  return response.blob();
}

export interface ApiErrorBody {
  error: { code: string; message: string; detail?: Record<string, unknown> };
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly detail: Record<string, unknown> | undefined;

  constructor(status: number, body: ApiErrorBody) {
    super(body.error.message);
    this.name = 'ApiError';
    this.status = status;
    this.code = body.error.code;
    this.detail = body.error.detail;
  }

  /** Somebody else wrote first. `detail.current` is the row as it now stands. */
  get isStale(): boolean {
    return this.code === 'stale' || this.code === 'conflict';
  }

  get needsSignIn(): boolean {
    return this.code === 'unauthenticated';
  }
}

/**
 * UUIDv7 — time-ordered, collision-free across offline devices. Same layout as
 * api/src/lib/ids.ts; rows created offline are minted here and keep their id
 * when they sync.
 */
export function uuidv7(now: number = Date.now()): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  const ms = BigInt(now);
  bytes[0] = Number((ms >> 40n) & 0xffn);
  bytes[1] = Number((ms >> 32n) & 0xffn);
  bytes[2] = Number((ms >> 24n) & 0xffn);
  bytes[3] = Number((ms >> 16n) & 0xffn);
  bytes[4] = Number((ms >> 8n) & 0xffn);
  bytes[5] = Number(ms & 0xffn);
  bytes[6] = (bytes[6]! & 0x0f) | 0x70;
  bytes[8] = (bytes[8]! & 0x3f) | 0x80;
  const hex = [...bytes].map((b) => b.toString(16).padStart(2, '0'));
  return `${hex.slice(0, 4).join('')}-${hex.slice(4, 6).join('')}-${hex
    .slice(6, 8)
    .join('')}-${hex.slice(8, 10).join('')}-${hex.slice(10, 16).join('')}`;
}

/**
 * The session token lives in memory only. The long-lived device credential is
 * the thing that persists (IndexedDB, step 3) and the PIN unlocks it locally —
 * the PIN itself is never stored, transmitted or displayed.
 */
let sessionToken: string | null = null;

export function setSessionToken(token: string | null): void {
  sessionToken = token;
}

/**
 * This tablet's registration credential, loaded from the vault at start-up and
 * sent on every request as `x-tablet-credential`. The Worker uses it to know
 * WHICH registered tablet is asking: to list who can log in before anyone has a
 * PIN here, and to let an owner signed in on it add people without a code.
 */
let tabletCredential: string | null = null;

export function setTabletCredential(credential: string | null): void {
  tabletCredential = credential;
}

export function hasSession(): boolean {
  return sessionToken !== null;
}

/** For the one request that sends a raw body rather than JSON: the signature PNG. */
export function currentToken(): string | null {
  return sessionToken;
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  /** Reuse an id when replaying a queued write, so the replay is idempotent. */
  clientOpId?: string;
  signal?: AbortSignal;
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const method = options.method ?? 'GET';
  const headers: Record<string, string> = {};

  if (sessionToken) headers.authorization = `Bearer ${sessionToken}`;
  if (tabletCredential) headers['x-tablet-credential'] = tabletCredential;
  if (options.body !== undefined) headers['content-type'] = 'application/json';
  if (method !== 'GET') headers['x-client-op-id'] = options.clientOpId ?? uuidv7();

  const init: RequestInit = { method, headers };
  if (options.body !== undefined) init.body = JSON.stringify(options.body);
  if (options.signal) init.signal = options.signal;

  const response = await send(`${BASE}${path}`, init);

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  const parsed: unknown = text ? JSON.parse(text) : null;

  if (!response.ok) {
    throw new ApiError(response.status, parsed as ApiErrorBody);
  }
  return parsed as T;
}

/**
 * A raw body — a signature PNG, a receipt JPEG — rather than JSON. base64
 * would inflate the image by a third, and the whole point of the R2 rule is
 * that a picture never travels as a data URL. Carries the op id like any other
 * mutation, so a replayed upload is one upload.
 */
export async function uploadBlob<T>(
  path: string,
  blob: Blob,
  contentType: string,
  clientOpId?: string,
): Promise<T> {
  const headers: Record<string, string> = {
    'content-type': contentType,
    'x-client-op-id': clientOpId ?? uuidv7(),
  };
  if (sessionToken) headers.authorization = `Bearer ${sessionToken}`;
  if (tabletCredential) headers['x-tablet-credential'] = tabletCredential;

  const response = await send(`${BASE}${path}`, { method: 'POST', headers, body: blob });
  const text = await response.text();
  const parsed: unknown = text ? JSON.parse(text) : null;
  if (!response.ok) throw new ApiError(response.status, parsed as ApiErrorBody);
  return parsed as T;
}

/** fetch, telling the connectivity module what happened. Any answer at all is a signal. */
async function send(url: string, init: RequestInit): Promise<Response> {
  try {
    const response = await fetch(url, init);
    noteOnline();
    return response;
  } catch (err) {
    if (err instanceof TypeError) noteOffline();
    throw err;
  }
}

export const api = {
  health: () =>
    request<{
      ok: boolean;
      checks: Record<string, { ok: boolean; detail?: string }>;
      businessDate: string;
      timezone: string;
      at: string;
    }>('/health'),

  me: () =>
    request<{
      user: { id: string; name: string; role: 'owner' | 'clerk' };
      permissions: string[];
      site: { id: string; timezone: string };
      today: string;
      isSunday: boolean;
      defaultPayRun: { from: string; to: string };
    }>('/me'),

  reference: () =>
    request<{
      today: string;
      plants: { id: string; code: string; name: string; cost_code_id: string | null }[];
      miscCategories: { id: string; name: string; requires_plant: number }[];
      costCodes?: { id: string; code: string; name: string; is_active: number }[];
      contributionRates: {
        epf_employee_pct_x100: number;
        epf_employer_pct_x100: number;
        etf_pct_x100: number;
        effective_from: string;
      } | null;
    }>('/reference'),

  /** Poll every 20–30s. Never move the view on its own — prompt instead. */
  changes: (since: number) =>
    request<{
      changes: { seq: number; scope: string; kind: string; at: string }[];
      seq: number;
    }>(`/changes?since=${since}`),
  // ------------------------------------------------------------ auth
  // The credential is the 32-byte token the PIN just unwrapped locally. It is
  // sent once, over TLS, and held nowhere. The PIN itself never leaves the
  // device in any form.
  unlock: (credential: string) =>
    request<{
      token: string;
      expiresAt: string;
      user: { id: string; name: string; role: 'owner' | 'clerk' };
      permissions: string[];
      device: { id: string; label: string };
    }>('/auth/unlock', { method: 'POST', body: { credential } }),

  lock: () => request<void>('/auth/lock', { method: 'POST' }),

  // ------------------------------------------------------- tablets
  /** A new tablet redeems the owner's tablet code. Registers SHA-256(T) only. */
  registerTablet: (code: string, credentialHash: string, label: string) =>
    request<{ tabletId: string; label: string }>('/auth/register-tablet', {
      method: 'POST',
      body: { code, credentialHash, label },
    }),

  /**
   * Who can log in, with each person's PIN-wrapped credential, for this
   * registered tablet to cache. `pin` is null for a login with no PIN yet.
   */
  logins: () =>
    request<{
      tablet: { id: string; label: string };
      logins: { id: string; name: string; role: 'owner' | 'clerk'; pin: PinWrap | null }[];
    }>('/auth/logins'),

  /**
   * The last owner's way back in, and how the first tablet is issued: one
   * recovery code registers the tablet and sets his PIN.
   */
  recover: (code: string, label: string, tabletCredentialHash: string, pin: PinWrap) =>
    request<{
      tabletId: string;
      label: string;
      user: { id: string; name: string; role: 'owner' | 'clerk' };
    }>('/auth/recover', {
      method: 'POST',
      body: { code, label, tabletCredentialHash, pin },
    }),

  /** A signed-in owner registers the tablet he is holding, linking the PINs already on it. */
  registerThisTablet: (label: string, credentialHash: string, deviceIds: string[]) =>
    request<{ tabletId: string; label: string }>('/auth/register-this-tablet', {
      method: 'POST',
      body: { label, credentialHash, deviceIds },
    }),

  listTablets: () => request<{ tablets: TabletRow[] }>('/tablets'),

  revokeTablet: (id: string) => request<void>(`/tablets/${id}`, { method: 'DELETE' }),

  createTabletRegistration: (label: string) =>
    request<{ id: string; code: string; expiresAt: string; label: string }>('/auth/tablet-registrations', {
      method: 'POST',
      body: { label },
    }),

  listTabletRegistrations: () =>
    request<{ registrations: { id: string; label: string | null; expires_at: string; created_at: string }[] }>(
      '/auth/tablet-registrations',
    ),

  cancelTabletRegistration: (id: string) =>
    request<void>(`/auth/tablet-registrations/${id}`, { method: 'DELETE' }),

  // ------------------------------------- owner: logins, devices, enrolments
  listUsers: () =>
    request<{ users: UserRow[]; allPermissions: string[] }>('/users'),

  /** A new login comes with its PIN, wrapped on this phone. */
  createUser: (body: { name: string; role: 'owner' | 'clerk'; permissions: string[]; pin: PinWrap }) =>
    request<UserRow>('/users', { method: 'POST', body }),

  updateUser: (
    id: string,
    body: {
      name?: string;
      role?: 'owner' | 'clerk';
      isActive?: boolean;
      permissions?: string[];
      /** A new PIN. The old credential and that person's sessions die with it. */
      pin?: PinWrap;
      rowVersion?: number;
    },
  ) => request<UserRow>(`/users/${id}`, { method: 'PATCH', body }),

  disableUser: (id: string) => request<void>(`/users/${id}`, { method: 'DELETE' }),

  listDevices: () =>
    request<{ devices: DeviceRow[]; currentDeviceId: string }>('/devices'),

  revokeDevice: (id: string) => request<void>(`/devices/${id}`, { method: 'DELETE' }),

  createEnrolment: (userId: string, label: string) =>
    request<{ id: string; code: string; expiresAt: string; user: string }>('/auth/enrolments', {
      method: 'POST',
      body: { userId, label },
    }),

  listEnrolments: () => request<{ enrolments: EnrolmentRow[] }>('/auth/enrolments'),

  cancelEnrolment: (id: string) =>
    request<void>(`/auth/enrolments/${id}`, { method: 'DELETE' }),

  issueRecoveryCode: (userId?: string) =>
    request<{ code: string; user: string }>('/auth/recovery-code', {
      method: 'POST',
      body: userId ? { userId } : {},
    }),
};

export interface UserRow {
  id: string;
  name: string;
  role: 'owner' | 'clerk';
  isActive: boolean;
  permissions: string[];
  rowVersion: number;
  deviceCount: number;
  hasRecoveryCode?: boolean;
  hasPin: boolean;
  pinSetAt: string | null;
}

/** A person's PIN-wrapped credential, as the phone made it and the server keeps it. */
export interface PinWrap {
  credentialHash: string;
  wrapped: string;
  salt: string;
  iv: string;
  iterations: number;
}

export interface DeviceRow {
  id: string;
  label: string;
  user_id: string;
  user_name: string;
  user_role: 'owner' | 'clerk';
  enrolled_at: string;
  last_unlock_at: string | null;
  locked_until: string | null;
  tablet_id: string | null;
  tablet_label: string | null;
}

export interface TabletRow {
  id: string;
  label: string;
  registeredAt: string;
  lastSeenAt: string | null;
  rowVersion: number;
  people: { userId: string; name: string; lastUnlockAt: string | null }[];
}

export interface EnrolmentRow {
  id: string;
  label: string | null;
  user_id: string;
  user_name: string;
  expires_at: string;
  created_at: string;
}
